/* Feb-2026 · Retroactive Sonnet Review admin panel.
 *
 * Lets the operator queue GPT-5.4 + Opus 4.7 review passes on every
 * finished chapter of a project, then browse the stored reviews.
 * Two-way flow:
 *   1. Enter project_id → click "Queue reviews" → backend runs the
 *      concurrent-bounded review pipeline and returns a summary.
 *   2. GET /api/admin/retroactive-review/project/{pid} — table view
 *      grouped by chapter with each reviewer model's verdict + edits.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, Play, ChevronDown, ChevronRight, ScrollText } from 'lucide-react';
import { toast } from 'sonner';
import { retroactiveReviewApi } from '@/lib/api';

export default function RetroactiveReviewPanel() {
  const [pid, setPid]           = useState('');
  const [data, setData]         = useState(null);
  const [busy, setBusy]         = useState('');
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    if (!pid) return;
    setBusy('load');
    try {
      setData(await retroactiveReviewApi.get(pid));
    } catch (e) { toast.error(String(e).slice(0, 140)); }
    finally { setBusy(''); }
  };

  const queue = async () => {
    if (!pid) return;
    setBusy('queue');
    try {
      const r = await retroactiveReviewApi.queue(pid);
      toast.success(`Queued ${r.chapters_reviewed} chapters × ${r.reviewer_models?.length || 2} reviewers`);
      await load();
    } catch (e) { toast.error(String(e).slice(0, 140)); }
    finally { setBusy(''); }
  };

  return (
    <div className="space-y-4" data-testid="retroactive-review-panel">
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="mb-3 flex items-center gap-2">
          <ScrollText className="h-4 w-4 text-primary" />
          <h3 className="text-[13px] font-semibold">Retroactive Sonnet Review</h3>
          <span className="text-[10.5px] text-muted-foreground">
            Re-runs finished chapters through GPT-5.4 + Opus 4.7 and stores recommended edits.
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={pid}
            onChange={(e) => setPid(e.target.value)}
            placeholder="Project ID (e.g. proj_abc123…)"
            className="h-8 max-w-sm text-[11.5px]"
            data-testid="retro-review-pid-input"
          />
          <Button size="sm" variant="outline"
                  disabled={!pid || busy === 'load'}
                  onClick={load}
                  data-testid="retro-review-load">
            {busy === 'load' ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
            Load existing
          </Button>
          <Button size="sm"
                  disabled={!pid || busy === 'queue'}
                  onClick={queue}
                  data-testid="retro-review-queue">
            {busy === 'queue' ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Play className="mr-1 h-3 w-3" />}
            Queue reviews
          </Button>
        </div>
      </div>

      {data && (
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="mb-2 flex items-center gap-2">
            <span className="text-[12px] font-semibold">
              Project {data.project_id} · {data.review_count} review{data.review_count === 1 ? '' : 's'}
            </span>
          </div>
          {(!data.by_chapter || data.by_chapter.length === 0) ? (
            <div className="text-[11px] text-muted-foreground">
              No reviews yet — click "Queue reviews" to run GPT-5.4 + Opus 4.7 passes.
            </div>
          ) : data.by_chapter.map((ch) => {
            const isOpen = expanded[ch.chapter_id];
            return (
              <div key={ch.chapter_id}
                   className="mb-2 rounded border border-border/60"
                   data-testid={`retro-chapter-${ch.chapter_id}`}>
                <button
                  type="button"
                  onClick={() => setExpanded((x) => ({ ...x, [ch.chapter_id]: !isOpen }))}
                  className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-[11.5px] hover:bg-secondary"
                >
                  {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  <span className="font-mono-stat">{ch.chapter_id}</span>
                  <span className="ml-auto flex gap-1">
                    {ch.reviews.map((r) => (
                      <Badge key={r.id} variant="outline"
                             className={`text-[9.5px] ${
                               r.status === 'done'   ? 'border-emerald-300 text-emerald-800 dark:border-emerald-800/40 dark:text-emerald-200' :
                               r.status === 'error'  ? 'border-red-300 text-red-800 dark:border-red-800/40 dark:text-red-200' :
                               'border-amber-300 text-amber-800 dark:border-amber-800/40 dark:text-amber-200'
                             }`}>
                        {r.reviewer_model_id} · {r.status}
                      </Badge>
                    ))}
                  </span>
                </button>
                {isOpen && (
                  <div className="grid gap-2 border-t border-border/40 p-2 sm:grid-cols-2">
                    {ch.reviews.map((r) => (
                      <div key={r.id}
                           className="rounded bg-panel-header/30 p-2 text-[10.5px]"
                           data-testid={`retro-review-${r.id}`}>
                        <div className="mb-1 flex items-center gap-1.5 font-semibold">
                          {r.reviewer_model_id}
                          {r.route && <span className="text-[9.5px] font-normal text-muted-foreground">· {r.route}</span>}
                        </div>
                        {r.status === 'error' ? (
                          <div className="text-red-800 dark:text-red-200">Error: {r.error}</div>
                        ) : (
                          <>
                            <p className="mb-1 whitespace-pre-wrap">{(r.review_text || '').slice(0, 800)}
                              {r.review_text && r.review_text.length > 800 ? '…' : ''}
                            </p>
                            {r.recommended_edits?.length > 0 && (
                              <ul className="list-disc space-y-0.5 pl-4">
                                {r.recommended_edits.slice(0, 8).map((e, i) => (
                                  <li key={i}>{e}</li>
                                ))}
                              </ul>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
